/**
 * Messages d'erreur des API traduits à l'écran (lot 9 du plan PR #7, point « messages serveur »).
 *
 * Le serveur répond `{ error, code, params }` : `error` reste le texte français (journaux, anciens
 * écrans), `code` désigne la clé i18n `apiErrors.<code>` que l'écran affiche dans SA langue, avec
 * `params` pour les valeurs variables. UN seul mécanisme pour tout le dépôt — il prolonge celui des
 * challenges (`CHALLENGE_ERROR_KEYS`, décision du 30/09/2026) au lieu d'en recopier un par fichier.
 *
 * ── Comment le code est trouvé ────────────────────────────────────────────────
 * Le catalogue ci-dessous associe chaque code à son texte français. Le serveur n'a donc pas à
 * nommer le code à chaque refus : `apiErrorBody(message)` le retrouve à partir du message (y compris
 * pour un message venu d'une règle partagée : `accountContextFor`, `assertCanPilot`, constantes de
 * `plan-core`…). Un message avec des valeurs variables passe son code et ses `params` explicitement.
 *
 * Garde-fou : `tests/api-error-codes.test.ts` vérifie que chaque code a son texte FR (identique au
 * catalogue) ET EN dans `src/i18n/locales/*.json`, et qu'aucun refus littéral des fichiers traités
 * n'échappe au catalogue.
 *
 * Fichier PARTAGÉ client + serveur : aucune dépendance (ni base, ni i18next).
 */

export type ApiErrorParams = Record<string, string | number>;

export interface ApiErrorBody {
  error: string;
  code?: string;
  params?: ApiErrorParams;
}

/** Code → texte français (tel que le serveur l'envoie dans `error`, `{{param}}` pour les variables). */
export const API_ERROR_MESSAGES_FR = {
  // ── Authentification, accès ──
  not_authenticated: "Non authentifié.",
  user_not_found: "Utilisateur introuvable.",
  access_denied: "Accès refusé.",
  managers_only: "Accès réservé aux encadrants.",
  organizers_only: "Accès réservé aux organisateurs.",
  admins_only: "Réservé aux administrateurs.",
  out_of_scope: "Hors périmètre.",
  account_required: "Compte requis.",
  no_company: "Aucune société associée.",
  account_not_in_portfolio: "Ce compte n'est pas dans votre portefeuille.",
  plan_edit_forbidden: "Vous ne pouvez pas modifier le plan.",
  internal_error: "Erreur interne du serveur.",
  trial_decline_forbidden: "Seuls l'organisateur, le gestionnaire financier ou un administrateur peuvent refuser l'essai.",
  invalid_json: "Corps JSON invalide.",
  invalid_body: "Corps de requête invalide.",
  // Visualiseur admin (Q1 du 01/10/2026) : lecture seule pour le COO sur trois tables.
  table_access_denied: "Vous n'avez pas accès à cette table.",
  table_read_only: "Cette table est en lecture seule pour votre profil.",
  // Groupes de workflows N8N (chantier Booster, T1 — 16/10/2026).
  admin_only_setting: "Ce réglage est réservé aux administrateurs.",
  n8n_group_not_found: "Groupe de workflows introuvable.",
  n8n_default_required: "Il faut toujours un groupe de workflows par défaut : pour en changer, marquez un autre groupe comme groupe par défaut.",
  n8n_default_delete_forbidden: "Le groupe de workflows par défaut ne peut pas être supprimé : marquez d'abord un autre groupe comme groupe par défaut.",
  n8n_group_in_use: "Ce groupe de workflows est utilisé par {{count}} simulateur(s) : rattachez-les à un autre groupe avant de le supprimer.",
  // Simulateurs à identifiant fixe (Booster, 19/10/2026) : jamais supprimés.
  simulator_delete_forbidden: "Ce simulateur ne peut pas être supprimé : son identifiant est attendu par ses workflows N8N. Désactivez-le à la place.",
  // ── Cas de simulation (Q1 du 01/10/2026 : MNG modifie ses cas, n'en supprime aucun) ──
  target_edit_owner_only: "Vous ne pouvez modifier que les cas de simulation que vous avez créés.",
  target_delete_forbidden: "Votre profil ne supprime pas les cas de simulation.",
  // ── Désactivation d'un cas (Q1 du 01/10/2026) — `targets.archived_at` ──
  target_not_found: "Cas de simulation introuvable.",
  target_archive_owner_only: "Vous ne pouvez désactiver ou réactiver que les cas de simulation que vous avez créés.",
  target_archived: "Ce cas de simulation a été désactivé : il n'est plus proposé pour s'entraîner. Choisissez un autre cas.",
  target_archived_not_linkable: "Ce cas de simulation est désactivé : réactivez-le avant de l'utiliser.",
  target_in_use_campaign: "Ce cas est utilisé par la campagne « {{name}} », en préparation ou en cours : il ne peut pas être désactivé tant qu'elle n'est pas clôturée.",
  target_in_use_path: "Ce cas est utilisé par le parcours « {{name}} » : il ne peut pas être désactivé tant que le parcours n'est pas archivé.",
  target_in_use_tournament: "Ce cas est utilisé par le tournoi « {{name}} », en cours : il ne peut pas être désactivé tant qu'il n'est pas terminé.",
  // ── Persona partagée entre cas (D1 du 01/10/2026) : un MNG ne la modifie que si elle ne sert qu'à ses cas ──
  persona_shared_with_other_cases: "Cette persona est aussi utilisée par des cas créés par d'autres personnes : seul l'organisateur du compte peut la modifier.",
  target_in_use_duel: "Ce cas est utilisé par un duel en cours : il ne peut pas être désactivé tant que le duel n'est pas joué.",
  // Duel et tournoi (audit du 01/10/2026) : le cas doit être visible par le créateur (`isTargetVisibleTo`).
  target_not_available: "Ce cas de simulation n'est pas disponible : choisissez un autre cas.",
  // ── Audit avant production du 01/10/2026 : refus du code nouveau (absent de prod) ──
  target_kestio_not_found: "Cas générique introuvable.",
  target_invalid_id: "Identifiant de cas invalide.",
  target_fork_failed: "La copie du cas a échoué.",
  target_offer_required_create: "Une offre est obligatoire pour créer un cas de simulation.",
  target_industry_required: "Le secteur d'activité est obligatoire pour un cas B2B Sales.",
  target_company_size_required: "La taille de l'entreprise est obligatoire pour un cas B2B Sales.",
  invalid_context: "Contexte invalide.",
  collaborator_profile_not_allowed: "Vous ne pouvez pas créer un collaborateur avec ce profil.",
  licence_quota_reached: "Quota de licences atteint. Contactez votre administrateur pour obtenir des licences supplémentaires.",
  purchase_forbidden: "Achat réservé à l'organisateur, au gestionnaire financier ou à un administrateur du compte.",
  server_error: "Erreur serveur",
  invalid_session_id: "ID de session invalide.",
  access_not_allowed: "Accès non autorisé.",
  prep_delete_only_prep: "Cette route ne supprime que des sessions Coach Prepa.",
  prep_delete_finalized: "Impossible de supprimer une préparation finalisée.",
  invalid_id: "Identifiant invalide.",
  drill_start_params_required: "criterionKey, simulatorId (uuid) et origin (feedback|history|progress) requis",
  drill_no_active_sheet: "Aucune fiche d'exercice active pour ce critère.",
  drill_session_not_found: "Session d'exercices introuvable",
  drill_sheet_not_found: "Fiche d'exercice introuvable",
  drill_agent_missing: "Configuration N8N 'drillAgent' manquante (URL webhook à renseigner par Mariam dans n8n_workflows.drill_agent).",
  drill_n8n_error: "Erreur N8N lors de la création de la session d'exercices.",
  drill_n8n_invalid_response: "Réponse N8N invalide : client_secret manquant.",
  drill_unavailable: "Les exercices ne sont pas encore disponibles.",
  // ── Périmètre des personnes ──
  no_member_in_scope: "Aucun collaborateur de votre périmètre.",
  member_not_in_scope: "Ce collaborateur n'est pas dans votre périmètre.",
  member_not_in_campaign_team: "Ce collaborateur n'est pas dans votre équipe pour cette campagne.",
  team_not_in_scope: "Cette équipe n'est pas dans votre périmètre.",
  member_not_found: "Collaborateur introuvable.",
  user_required: "Utilisateur requis.",
  no_licence_in_selection: "Personne dans cette sélection n'a la licence de ce simulateur.",
  nobody_can_play: "Personne ne peut jouer ce simulateur : {{names}}. Ouvrez les licences avant de lancer.",
  participants_hr_manager_refused: "Les profils RH et gestionnaire ne peuvent pas être ajoutés comme participants d'un challenge.",
  // ── Saisie ──
  invalid_dates: "Dates invalides.",
  dates_required: "Dates requises.",
  end_before_start: "La fin précède le début.",
  invalid_start_date: "Date de début invalide (AAAA-MM-JJ).",
  invalid_day: "Jour invalide (AAAA-MM-JJ).",
  invalid_period: "Période invalide.",
  invalid_duration: "Durée invalide.",
  invalid_status: "Statut invalide.",
  invalid_auto_launch: "Lancement automatique : valeur invalide.",
  invalid_weekdays: "Jours invalides : choisissez des jours du lundi (1) au vendredi (5).",
  members_and_dates_required: "Participants (500 au plus) et dates (AAAA-MM-JJ) requis.",
  simulator_and_members_required: "Simulateur et participants requis (500 au plus).",
  nothing_to_save: "Rien à enregistrer.",
  nothing_to_update: "Rien à modifier.",
  nothing_to_place: "Rien à placer.",
  unknown_type: "Type inconnu.",
  unknown_item: "Élément inconnu.",
  invalid_item: "Élément invalide.",
  item_not_in_campaign_account: "Cet élément n'appartient pas au compte de la campagne.",
  criterion_required: "Critère requis.",
  name_required: "Nom requis.",
  title_required: "Le titre est requis.",
  simulator_required: "Simulateur requis.",
  simulator_required_field: "Le simulateur est requis.",
  simulator_not_found: "Simulateur introuvable.",
  simulator_required_for_sessions: "Simulateur requis pour des sessions.",
  simulator_required_for_campaign: "Simulateur requis pour une campagne.",
  no_scoring_criterion: "Aucun critère d'évaluation disponible pour ce simulateur.",
  choose_one_stake: "Choisissez au moins un enjeu.",
  stakes_required: "Enjeux requis.",
  choose_rhythm_first: "Choisissez d'abord un rythme.",
  rhythm_all_needs_day: "Ligne « Tous les participants » : choisissez au moins un jour, ou « Aucune session » dans le rythme.",
  invalid_rhythm: "Rythme invalide : de 0 (aucune session) à 5 sessions par semaine.",
  // ── Sessions planifiées ──
  session_not_found: "Session introuvable.",
  session_done_cannot_move: "Une session réalisée ne se déplace plus.",
  // Q5 (30/09/2026) : le Plan ne pose aucune session au-delà de la fin d’accès du compte.
  session_after_access_end: "Cette date tombe après la fin d'accès du compte : aucune session ne peut y être prévue.",
  session_done_cannot_delete: "Une session réalisée ne se supprime pas.",
  // ── Campagnes ──
  campaign_not_found: "Campagne introuvable.",
  campaign_not_in_scope: "Cette campagne n'est pas dans votre périmètre.",
  campaign_account_level: "Cette campagne est posée au niveau du compte : elle se pilote depuis l'espace organisateur.",
  campaign_other_author: "Cette campagne a été créée par quelqu'un d'autre : seul son auteur ou un organisateur peut la piloter.",
  campaign_create_forbidden: "Vous ne pouvez pas créer de campagne.",
  campaign_name_required: "Le nom de la campagne est requis.",
  campaign_already_launched: "Cette campagne est déjà lancée.",
  campaign_launched_dates_in_plan: "Cette campagne est lancée : ses dates se changent dans le Plan.",
  campaign_launched_name_locked: "Cette campagne est lancée : son nom n'est plus modifiable.",
  campaign_closed_days_locked: "Cette campagne est terminée : ses jours ne se modifient plus.",
  campaign_closed_locked: "Une campagne terminée ne se modifie plus.",
  campaign_start_locked: "Une campagne en cours ne se décale plus : seule sa fin peut bouger.",
  campaign_cannot_shorten: "Une campagne lancée peut être prolongée, jamais raccourcie.",
  campaign_criterion_locked: "La compétence d'une campagne lancée ne change plus : pour en travailler une autre, créez une nouvelle campagne.",
  campaign_level_down: "Le niveau visé d'une campagne lancée ne peut que monter.",
  campaign_not_launched_close: "Cette campagne n'est pas lancée : supprimez-la ou lancez-la.",
  campaign_delete_draft_only: "Seule une campagne en brouillon peut être supprimée. Clôturez-la plutôt.",
  campaign_incomplete: "Le dispositif n'est pas complet.",
  campaign_exclude_invalid: "Campagne à exclure invalide.",
  campaign_step_not_found: "Brique introuvable.",
  campaign_simulator_required_for_challenge: "Le simulateur de la campagne est requis pour créer son challenge.",
  path_not_in_campaign_account: "Ce parcours n'est pas du compte de la campagne.",
  no_participant_to_invite: "Aucun participant à inviter.",
  too_many_recipients: "Cinquante destinataires au plus par envoi.",
  // ── Bilan de campagne ──
  report_not_in_scope: "Ce bilan n'est pas dans votre périmètre.",
  report_send_managers_only: "L'envoi du bilan est réservé à l'encadrant.",
  report_pdf_managers_only: "Le PDF du bilan est réservé à l'encadrant.",
  report_recipient_required: "Indiquez au moins un destinataire.",
  report_recipients_refused: "Le bilan ne s'envoie qu'aux personnes de ce compte client ou à vous-même ({{emails}}).",
  // ── Challenges et parcours depuis le plan ──
  challenge_not_found: "Challenge introuvable.",
  challenge_profile_cannot_edit: "Votre profil ne modifie pas les challenges : un organisateur ou leur auteur peut le faire.",
  challenge_other_author: "Ce challenge a été créé par quelqu'un d'autre : seul son auteur ou un organisateur peut le modifier.",
  challenge_ended_locked: "Un challenge terminé ne se modifie plus.",
  path_not_found: "Parcours introuvable.",
  // ── Tournois ──
  tournament_not_found: "Tournoi introuvable.",
  tournament_manage_forbidden: "Seul l'auteur du tournoi ou un organisateur du compte peut le gérer.",
  tournament_already_started: "Le tournoi a déjà démarré.",
  tournament_started: "Tournoi déjà démarré.",
  tournament_full: "Tournoi complet.",
  tournament_registration_closed: "Les inscriptions sont closes.",
  tournament_other_company: "Tournoi réservé à une autre entreprise.",
  tournament_min_participants: "Il faut au moins 2 participants.",
  tournament_profile_excluded: "Ce profil ne participe pas aux tournois.",
  tournament_add_own_team_only: "Vous ne pouvez ajouter que des membres de votre équipe.",
  tournament_remove_own_team_only: "Vous ne pouvez retirer que des membres de votre équipe.",
  // ── Portefeuilles consultants (ADM) ──
  consultant_not_found: "Consultant introuvable.",
  not_a_consultant: "Ce compte n'est pas un consultant.",
  // ── Réglages ──
  unknown_message_register: "Registre inconnu : « tu » ou « vous ».",
  // ── Matrice des critères (comportement, aide) ──
  behavior_official_criteria_only: "Le comportement ne concerne que les 37 critères notés officiels, dans leur version commune d'un simulateur.",
  criterion_field_too_long: "{{field}} : {{max}} caractères au plus",
  // ── Fiche collaborateur (droits, photo, identité, responsable) ──
  change_not_allowed: "Modification non autorisée.",
  cannot_deactivate_self: "Vous ne pouvez pas vous désactiver.",
  cannot_change_own_profile: "Vous ne pouvez pas modifier votre propre profil.",
  own_team_members_only: "Vous ne pouvez gérer que les collaborateurs de votre équipe.",
  org_edits_org_only: "Seul un organisateur peut modifier un organisateur.",
  profile_picture_invalid: "Photo de profil invalide.",
  profile_picture_too_large: "Photo de profil trop lourde.",
  profile_picture_format: "Format de photo non accepté (JPEG, PNG ou WebP).",
  profile_picture_https: "URL de photo de profil invalide (HTTPS requis).",
  first_name_required: "Le prénom est requis.",
  last_name_required: "Le nom est requis.",
  unknown_spoken_language: "Langue d'entraînement inconnue.",
  invalid_email: "Adresse email invalide.",
  email_already_used: "Cet email est déjà utilisé.",
  manager_not_found: "Manager introuvable.",
  manager_self: "Un utilisateur ne peut pas être son propre manager.",
  manager_not_in_team: "Le nouveau manager doit appartenir à votre équipe.",
  manager_other_entity: "Le manager doit appartenir à la même entité.",
  manager_profile_invalid: "Le manager doit avoir un profil Organisateur ou Manager.",
  manager_loop: "Cette assignation créerait une boucle hiérarchique.",
  // ── Matrice des critères (ADM) ──
  admin_account_not_linked: "Compte administrateur non rattaché à une société.",
  criterion_invalid_id: "Identifiant de critère invalide.",
  criterion_not_found: "Critère introuvable.",
  invalid_simulator_id: "Identifiant de simulateur invalide.",
  enable_value_required: "Précisez s'il faut activer ou désactiver.",
  invalid_context_type: "Type de contexte invalide (valeurs : {{values}}).",
  criterion_field_invalid: "{{field}} : valeur invalide.",
  criterion_key_required: "La clé du critère est requise.",
  criterion_invalid_key: "Clé invalide (3 à 60 caractères : lettres, chiffres, tiret bas).",
  criterion_label_required: "Le libellé français est requis.",
  criterion_label_length: "Le libellé français doit faire de 2 à 200 caractères.",
  criterion_description_length: "La description doit faire de {{min}} à {{max}} caractères (ou rester vide).",
  criterion_invalid_theme: "Thème invalide (valeurs : {{values}}).",
  criterion_invalid_weight: "Le poids doit être un nombre entre 0 et 1.",
  criterion_invalid_type: "Le type doit être « score » ou « boolean ».",
  criterion_key_exists: "Une clé identique existe déjà.",
  criterion_core_cannot_deactivate: "Le critère « {{key}} » est structurel (utilisé par les workflows N8N) : il ne peut pas être désactivé.",
  // ── Cockpit encadrant (relances, félicitations, fiche collaborateur) ──
  no_recipient: "Aucun destinataire.",
  too_many_recipients_max: "Trop de destinataires (maximum {{max}}).",
  member_not_specified: "Collaborateur non précisé.",
  // Décision D1 du 01/10/2026 : une seule équipe dans tout Pilot, filiales exclues.
  recipient_in_subsidiary: "Les collaborateurs des filiales ne font pas partie de votre équipe dans Pilot : aucun message n'a été envoyé.",
  // ── Banque de cas : réécriture du texte d'un cas (fiche admin, 05/10/2026) ──
  banque_case_not_found: "Cas introuvable.",
  banque_simulator_not_playable: "Simulateur non jouable sur ce cas.",
  banque_no_text: "Aucun texte à enregistrer.",
  banque_unknown_language: "Langue inconnue.",
  banque_agent_text_invalid: "Balises invalides.",
  banque_skin_closed: "L'habillage sectoriel n'est pas ouvert.",
  // ── Documents légaux : versions des CGV/CGU et du DPA (lot 1, 07/10/2026) ──
  legal_pdf_invalid: "Le fichier n'est pas un PDF.",
  legal_pdf_too_large: "Le PDF dépasse 15 Mo.",
  legal_kind_invalid: "Type de document inconnu.",
  legal_version_required: "Indiquez un numéro de version (40 caractères au plus).",
  legal_effective_day_invalid: "Date d'entrée en vigueur invalide.",
  legal_version_exists: "Ce numéro de version existe déjà pour ce document.",
  legal_effective_day_taken: "Une version de ce document entre déjà en vigueur ce jour-là.",
  legal_effective_day_past: "La date d'entrée en vigueur ne peut pas être passée.",
  legal_effective_day_before_existing: "Une version de ce document entre en vigueur plus tard : retirez-la d'abord, ou choisissez une date postérieure.",
  legal_already_withdrawn: "Cette version est déjà retirée.",
  legal_version_locked: "Cette version est déjà entrée en vigueur : elle ne peut plus être retirée.",
  legal_version_not_found: "Version introuvable.",
  legal_version_not_withdrawn: "Cette version n'est pas retirée.",
  // ── Inscription : acceptation des CGV/CGU et du DPA, SIREN (lot 3, 07/10/2026) ──
  signup_siren_invalid: "Le numéro SIREN n'est pas valide : il compte 9 chiffres.",
  signup_foreign_registration_required: "Indiquez le numéro d'immatriculation ou de TVA de la société.",
  legal_documents_unavailable: "La création de compte est momentanément indisponible. Contactez Kestio.",
  legal_acceptance_required: "Acceptez les CGV/CGU et le DPA pour créer votre compte.",
  legal_documents_changed: "Les CGV/CGU ou le DPA viennent d'être mis à jour : relisez-les et cochez à nouveau les cases.",
  // ── Acceptation du DPA dans l'application (lot 5, 07/10/2026) ──
  legal_accept_org_only: "Seul l'organisateur du compte peut accepter le DPA.",
  legal_dpa_acceptance_required: "Acceptez le DPA pour continuer.",
  // ── Simulateurs d'une licence d'abonnement attribuée depuis le pool (incident ALTIOS, 14/10/2026) ──
  licence_simulators_required: "Choisissez les simulateurs de cette licence ({{max}} au plus).",
  licence_simulators_too_many: "Cette licence donne accès à {{max}} simulateur(s) au plus.",
  licence_simulators_unknown: "Un des simulateurs choisis n'est pas disponible.",
  licence_simulators_not_choosable: "Les simulateurs de cette licence ne se choisissent pas.",
  licence_not_assigned: "Cette licence n'est pas attribuée à ce collaborateur.",
  // ── Coachs inclus dans toutes les formules (14/10/2026) ──
  coach_access_denied: "Les coachs sont réservés aux personnes qui ont une licence ou un pack actif.",
  coach_credits_exhausted: "Vous n'avez plus de crédit disponible : les coachs seront de nouveau accessibles avec un nouveau pack ou au renouvellement de votre abonnement.",
  // ── Sécurité des achats (lot S du chantier pricing Start / Grow, 10/2026) ──
  route_removed: "Cette adresse n'est plus disponible.",
  plan_not_purchasable: "Ce produit n'est pas disponible à l'achat.",
  quantity_too_high: "Vous pouvez commander au plus {{max}} unités de ce produit à la fois.",
  pack_once_per_order: "Ce pack ne peut être acheté qu'une fois par commande.",
} as const;

export type ApiErrorCode = keyof typeof API_ERROR_MESSAGES_FR;

/** Codes nés avant ce catalogue, traduits par leurs propres clés (challenges, participants, campagnes). */
export const LEGACY_API_ERROR_KEYS: Record<string, string> = {
  participant_not_trainee: "participants.notTrainee",
  campaign_closed_members: "campaign.errors.closedMembers",
};

const CODE_BY_MESSAGE = new Map<string, string>(
  Object.entries(API_ERROR_MESSAGES_FR).filter(([, m]) => !m.includes("{{")).map(([c, m]) => [m, c]),
);

/** Code du catalogue pour un message français exact, sinon `undefined`. */
export function apiErrorCodeFor(message: string | null | undefined): string | undefined {
  return message ? CODE_BY_MESSAGE.get(message) : undefined;
}

/** Remplace `{{nom}}` par les valeurs de `params`. */
export function fillApiErrorParams(text: string, params?: ApiErrorParams): string {
  if (!params) return text;
  return text.replace(/\{\{(\w+)\}\}/g, (all, k: string) => (params[k] !== undefined ? String(params[k]) : all));
}

/**
 * Corps d'une réponse d'erreur : `error` (français), `code` (donné, sinon retrouvé à partir du
 * message), `params`. Avec un `code` du catalogue et des `params`, `error` est le texte du
 * catalogue rempli.
 */
export function apiErrorBody(message: string, opts?: { code?: string | null; params?: ApiErrorParams }): ApiErrorBody {
  const code = opts?.code || apiErrorCodeFor(message);
  const params = opts?.params;
  const template = code && code in API_ERROR_MESSAGES_FR ? API_ERROR_MESSAGES_FR[code as ApiErrorCode] : null;
  const error = template && params ? fillApiErrorParams(template, params) : message;
  return { error, ...(code ? { code } : {}), ...(params ? { params } : {}) };
}

/** Clé i18n d'un code (`apiErrors.<code>` ou clé historique). */
export function apiErrorI18nKey(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  if (LEGACY_API_ERROR_KEYS[code]) return LEGACY_API_ERROR_KEYS[code];
  return code in API_ERROR_MESSAGES_FR ? `apiErrors.${code}` : undefined;
}

/**
 * Erreur métier porteuse d'un code (services qui lèvent au lieu de répondre). Le handler la
 * transforme en réponse avec `apiErrorBody(err.message, { code: err.code, params: err.params })`.
 */
export class CodedError extends Error {
  constructor(public code: ApiErrorCode, public params?: ApiErrorParams, public status = 400) {
    super(fillApiErrorParams(API_ERROR_MESSAGES_FR[code], params));
  }
}
